import first from 'lodash/first'
import isNull from 'lodash/isNull'
import find from 'lodash/find'
import moment from 'moment-timezone'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import { Field, Form, Formik } from 'formik'

import { FormikPersonAutocomplete } from 'civic-champs-shared/core/add-person/PersonAutocomplete'
import { PersonSuggestion } from 'civic-champs-shared/api/hooks/useFetchPersonSuggestions'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { AddPersonFlow } from 'civic-champs-shared/core/add-person'
import useAddPerson from 'civic-champs-shared/api/hooks/useAddPerson'
import Loading from 'civic-champs-shared/core/Loading'
import StepModalContainer, { useModalStyles } from 'civic-champs-shared/core/StepModalContainer'
import { timezones } from 'civic-champs-shared/helpers/timezones'
import OutlinedButton from 'civic-champs-shared/core/OutlinedButton'
import ContainedButton from 'civic-champs-shared/core/ContainedButton'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'

import { useGroups } from 'group/hooks/useGroups'
import { GroupTypeCode } from 'Event/components/opportunity/GroupPicker/types'
import yup from 'civic-champs-shared/utils/yup'
import { StyledKeyboardDatePicker } from 'components/StyledFormikDateTimePickers'
import { StyledAutocomplete } from 'components/StyledAutocomplete'
import useFetchPrograms from 'new-mentorship/hooks/useFetchPrograms'
import { MentorshipProgram, Tag } from 'new-mentorship/types'
import { TagAutocomplete } from 'components/TagAutoComplete'
import { TagAutocomplete as NewTagAutocomplete } from 'tags/components/TagAutoComplete'
import useTagManagement from 'new-mentorship/hooks/useTagsManagement'

import { Person } from 'people/interface'
import { useFeatureEnabled } from 'core/feature/hooks'

const getInitialPersonCreateValues = (input: string) => {
  if (input.trim()) {
    const emailMatch = input.match(/[^\s]+@[^\s]+/)
    const email = first(emailMatch) || ''
    input = input.replace(email, '')
    const phoneMatch = input.match(/^\+?\d+$|^\+?\d+[^\s]|[^\s]\+?\d+[^\s]/)
    const phone = first(phoneMatch) || ''
    const names = input.replace(phone, '').trim().replace(/\s+/, ' ').split(' ')
    return {
      givenName: names.shift(),
      familyName: names.join(' '),
      email,
      phone,
      home: null,
      birthday: null,
      sendInvite: true,
    }
  }
}

export interface CreateMatchForm {
  program: MentorshipProgram | null
  mentor: Omit<PersonSuggestion, 'pubnubId'> | null
  mentee: Omit<PersonSuggestion, 'pubnubId'> | null
  startedAt: Date | null
  stoppedAt: Date | null
  meetingsTimezone: { name: string; value: string } | null
  tags: Tag[]
}

const validationSchema = yup.object({
  program: yup.object().nullable().required('A Program is required. Please select a Program.'),
  mentor: yup.object().nullable().required('A Mentor is required. Please select a Mentor.'),
  mentee: yup
    .object()
    .nullable()
    .required('A Mentee is required. Please Select a Mentee.')
    .when('mentor', (mentor: PersonSuggestion, schema: any) => {
      return schema.test({
        test: (mentee: PersonSuggestion) => {
          return mentor?.id !== mentee?.id
        },
        message: 'Cannot mentor yourself',
      })
    }),
  startedAt: yup.date().nullable().required('A Starting Date is required. Please provide the Starting Date.'),
  meetingsTimezone: yup.string().nullable().required('A Time Zone is required. Please select a Time Zone.'),
  stoppedAt: yup.date().nullable(),
})

export default function MatchFlowEditor(props: any) {
  const org = useCurrentOrg()
  const {
    id,
    showing,
    submit,
    cancel,
    label,
    editable = true,
    initialValues = {
      program: null,
      mentor: null,
      mentee: null,
      startedAt: null,
      stoppedAt: null,
      meetingsTimezone: find(timezones, { value: org.timeZone }),
      tags: [],
    } as CreateMatchForm,
  } = props

  const classes = useModalStyles()
  const [tags, setTags] = useState<Tag[]>([])
  const [tagsLoading, setTagsLoading] = useState<boolean>(false)
  const { groups, loading: groupsLoading } = useGroups()
  const { mentorGroupId, menteeGroupId } = useMemo(
    () => ({
      mentorGroupId: find(groups, { groupType: { code: GroupTypeCode.Mentor } })?.id,
      menteeGroupId: find(groups, { groupType: { code: GroupTypeCode.Mentee } })?.id,
    }),
    [groups],
  )
  const [fetchPrograms, { result: programs, loading: programsLoading }] = useFetchPrograms()

  const [addNewMentor] = useAddPerson({ groupId: mentorGroupId })
  const [addNewMentee] = useAddPerson({ groupId: menteeGroupId })

  const handleMentorAdd = (userData: any) =>
    addNewMentor({
      type: 'NEW_USER',
      ...userData,
    })

  const handleMenteeAdd = (userData: any) =>
    addNewMentee({
      type: 'NEW_USER',
      ...userData,
    })

  useEffect(() => {
    fetchPrograms()
  }, [fetchPrograms])
  const { getOrgTags } = useTagManagement()
  const refreshTags = () => {
    setTagsLoading(true)
    getOrgTags().then(tags => {
      setTags(tags)
      setTagsLoading(false)
    })
  }
  useEffect(refreshTags, [getOrgTags])

  const showAddPerson = useShowPrompt(AddPersonFlow)
  const isNewTagsEnabled = useFeatureEnabled('NewTags')

  const handleSubmit = useCallback(
    ({ program, mentor, mentee, startedAt, stoppedAt, tags, meetingsTimezone }: CreateMatchForm) => {
      return submit({
        programId: program?.id,
        mentorId: mentor?.id,
        menteeId: mentee?.id,
        startedAt: isNull(startedAt) ? startedAt : moment(startedAt).format('YYYY-MM-DD'),
        stoppedAt: isNull(stoppedAt) ? stoppedAt : moment(stoppedAt).format('YYYY-MM-DD'),
        meetingsTimezone: meetingsTimezone?.value,
        tags,
      }).then(() => cancel()) // TODO ...not really how prompts are intended to work
    },
    [cancel, submit],
  )

  const getHandleCreate = (setFieldValue: any, isMentor: boolean) => (input: string) => {
    showAddPerson({
      submit: ({ userData, ...data }: any) =>
        (isMentor ? handleMentorAdd : handleMenteeAdd)({ ...data, ...userData }).then(
          ([{ person }]: [{ person: Person }]) => {
            const { id, givenName, familyName, email, phoneNumber } = person
            const contact = email || phoneNumber
            setFieldValue(isMentor ? 'mentor' : 'mentee', {
              id,
              givenName,
              familyName,
              contact,
              title: `${givenName} ${familyName} - ${contact}`,
              groupId: isMentor ? mentorGroupId : menteeGroupId,
            })
          },
        ),
      label: 'Add New ' + (isMentor ? 'Mentor' : 'Mentee'),
      initialValues: getInitialPersonCreateValues(input),
    })
  }

  return (
    <StepModalContainer showing={showing} cancel={cancel} steps={[label]} activeStep={0} disableEnforceFocus={true}>
      {groupsLoading || tagsLoading || programsLoading ? (
        <Loading />
      ) : (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ validateForm, submitForm, isSubmitting, setFieldValue, values }) => {
            return (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      name="program"
                      label={values.program ? 'Program *' : 'Select a Program *'}
                      component={StyledAutocomplete}
                      options={programs}
                      getOptionLabel={({ name }: MentorshipProgram) => name}
                      disabled={!editable || isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      name="meetingsTimezone"
                      label={values.meetingsTimezone ? 'Time Zone *' : 'Select a Time Zone *'}
                      component={StyledAutocomplete}
                      options={timezones}
                      getOptionLabel={({ name }: any) => name}
                      getOptionSelected={(option: any, value: any) => {
                        return option?.value === value?.value
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      onChange={() => setTimeout(validateForm, 100)}
                      name="mentor"
                      label={values.mentor ? 'Mentor' : 'Select a Mentor'}
                      groupId={mentorGroupId}
                      groupTitle="Mentors"
                      disabled={!editable || isSubmitting}
                      onCreate={getHandleCreate(setFieldValue, true)}
                      required={true}
                      component={FormikPersonAutocomplete}
                      addNewPersonText="+ Add a new Mentor"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      name="mentee"
                      label={values.mentee ? 'Mentee' : 'Select a Mentee'}
                      groupId={menteeGroupId}
                      groupTitle="Mentees"
                      disabled={!editable || isSubmitting}
                      onCreate={getHandleCreate(setFieldValue, false)}
                      required={true}
                      component={FormikPersonAutocomplete}
                      addNewPersonText="+ Add a new Mentee"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      name="startedAt"
                      fullWidth
                      label={values.startedAt ? 'Start Date *' : 'Enter a Starting Date *'}
                      iconColor="#0F5DB5"
                      component={StyledKeyboardDatePicker}
                      inputVariant="outlined"
                      format="MM/DD/YYYY"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      name="stoppedAt"
                      fullWidth
                      label={values.stoppedAt ? 'End Date' : 'Enter an End Date'}
                      iconColor="#0F5DB5"
                      component={StyledKeyboardDatePicker}
                      inputVariant="outlined"
                      format="MM/DD/YYYY"
                      clearable={true}
                      disabled={!id}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="tags"
                      label={values.tags?.length ? 'Tags' : 'Select Tags'}
                      options={tags}
                      addOption={(option: Tag) => {
                        setTags(prev => [...prev, option])
                      }}
                      component={isNewTagsEnabled ? NewTagAutocomplete : TagAutocomplete}
                      getOptionLabel={({ name }: Tag) => name}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  justify="space-between"
                  classes={{ root: classes.buttonsContainer }}
                >
                  <OutlinedButton disabled={isSubmitting} onClick={cancel}>
                    Cancel
                  </OutlinedButton>
                  <ContainedButton disabled={isSubmitting} onClick={submitForm}>
                    {id ? 'Save' : 'Add'}
                  </ContainedButton>
                </Grid>
              </Form>
            )
          }}
        </Formik>
      )}
    </StepModalContainer>
  )
}
